import {
  ALL_PRODUCTS_VIEW_PORTS_PAYLOAD_KEY,
  AllProductsViewPorts,
  DEFAULT_ALL_PRODUCTS_VIEW_PORTS,
  VIEW_PORT_KEYS,
  ViewPortKey,
  parseAllProductsViewPorts,
  parseDefaultViewPortKey,
  parseViewPortOrder,
  resolveDefaultViewMode,
} from './our-products-layout.util';

export const CATEGORY_PAGE_NAME_PAYLOAD_KEY = 'pageName';
/** Mobile-specific page title; falls back to desktop `pageName` when absent. */
export const CATEGORY_PAGE_NAME_MOBILE_PAYLOAD_KEY = 'pageNameMobile';
export const CATEGORY_VIEW_PORTS_PAYLOAD_KEY = ALL_PRODUCTS_VIEW_PORTS_PAYLOAD_KEY;
/** Mobile-specific view ports; falls back to desktop `allProductsViewPorts` when absent. */
export const CATEGORY_VIEW_PORTS_MOBILE_PAYLOAD_KEY = 'allProductsViewPortsMobile';

export type CategoryLayoutDevice = 'desktop' | 'mobile';

/** Phone min width for flexible-screen mobile layouts (Figma canvas). */
export const CATEGORY_LAYOUT_PHONE_MIN_PX = 320;
/** Phone max width from `_auth-responsive.scss` ($auth-phone-max = 26.875rem). */
export const CATEGORY_LAYOUT_PHONE_MAX_PX = 430;

/** Flexible-screen mobile layout: phone viewports only (320px–430px). */
export const CATEGORY_LAYOUT_PHONE_MEDIA_QUERY =
  '(min-width: 20rem) and (max-width: 26.875rem)';

/** Used by flexible-screen pages to pick mobile vs desktop layout payloads. */
export const CATEGORY_LAYOUT_MOBILE_MEDIA_QUERY = CATEGORY_LAYOUT_PHONE_MEDIA_QUERY;

export function resolveCategoryLayoutDeviceFromViewport(
  win: Window | null = typeof window !== 'undefined' ? window : null,
): CategoryLayoutDevice {
  if (!win?.matchMedia) {
    return 'desktop';
  }
  return win.matchMedia(CATEGORY_LAYOUT_MOBILE_MEDIA_QUERY).matches ? 'mobile' : 'desktop';
}

export function categoryPageNamePayloadKey(
  device: CategoryLayoutDevice = 'desktop',
): string {
  return device === 'mobile'
    ? CATEGORY_PAGE_NAME_MOBILE_PAYLOAD_KEY
    : CATEGORY_PAGE_NAME_PAYLOAD_KEY;
}

/** Category pages expose every view port until an admin layout says otherwise. */
export const DEFAULT_CATEGORY_VIEW_PORTS: AllProductsViewPorts = {
  ...DEFAULT_ALL_PRODUCTS_VIEW_PORTS,
};

export type CategoryViewPorts = AllProductsViewPorts;

export function categoryViewPortsPayloadKey(
  device: CategoryLayoutDevice = 'desktop',
): string {
  return device === 'mobile'
    ? CATEGORY_VIEW_PORTS_MOBILE_PAYLOAD_KEY
    : CATEGORY_VIEW_PORTS_PAYLOAD_KEY;
}

/**
 * Builds a payload slice that existing parsers can read.
 * Mobile uses `allProductsViewPortsMobile` when present, else desktop.
 */
function payloadForCategoryDevice(
  payload: Record<string, unknown> | null | undefined,
  device: CategoryLayoutDevice,
): Record<string, unknown> | null | undefined {
  if (!payload || device === 'desktop') {
    return payload;
  }
  const mobile = payload[CATEGORY_VIEW_PORTS_MOBILE_PAYLOAD_KEY];
  if (mobile && typeof mobile === 'object' && !Array.isArray(mobile)) {
    return { [ALL_PRODUCTS_VIEW_PORTS_PAYLOAD_KEY]: mobile };
  }
  return payload;
}

export function parseCategoryViewPorts(
  payload: Record<string, unknown> | null | undefined,
): CategoryViewPorts {
  return parseAllProductsViewPorts(payload);
}

export function parseCategoryViewPortsForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: CategoryLayoutDevice = 'desktop',
): CategoryViewPorts {
  return parseCategoryViewPorts(payloadForCategoryDevice(payload, device));
}

export function parseCategoryViewPortOrder(
  payload: Record<string, unknown> | null | undefined,
): ViewPortKey[] {
  return parseViewPortOrder(payload, VIEW_PORT_KEYS);
}

export function parseCategoryViewPortOrderForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: CategoryLayoutDevice = 'desktop',
): ViewPortKey[] {
  return parseCategoryViewPortOrder(payloadForCategoryDevice(payload, device));
}

export function parseCategoryDefaultViewPort(
  payload: Record<string, unknown> | null | undefined,
): ViewPortKey {
  return parseDefaultViewPortKey(
    payload,
    parseCategoryViewPorts(payload),
    VIEW_PORT_KEYS,
  );
}

export function parseCategoryDefaultViewPortForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: CategoryLayoutDevice = 'desktop',
): ViewPortKey {
  const devicePayload = payloadForCategoryDevice(payload, device);
  return parseDefaultViewPortKey(
    devicePayload,
    parseCategoryViewPorts(devicePayload),
    VIEW_PORT_KEYS,
  );
}

/** Default nested record written under either desktop or mobile view-ports key. */
export function defaultCategoryViewPortsRecord(): Record<string, unknown> {
  return {
    ...DEFAULT_CATEGORY_VIEW_PORTS,
    order: [...VIEW_PORT_KEYS],
    default: VIEW_PORT_KEYS.find((key) => DEFAULT_CATEGORY_VIEW_PORTS[key]) ?? VIEW_PORT_KEYS[0],
  };
}

/**
 * Supports both plain string page names and multilingual `{ en, fr, ... }` records.
 */
export function parseCategoryPageName(
  payload: Record<string, unknown> | null | undefined,
  preferredLang?: string,
): string {
  const value = payload?.[CATEGORY_PAGE_NAME_PAYLOAD_KEY];
  if (typeof value === 'string') {
    return value.trim();
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return '';
  }
  const record = value as Record<string, unknown>;
  const lang = (preferredLang || 'en').toLowerCase();
  const candidates = [lang, lang.split('-')[0], 'en', ...Object.keys(record)];
  for (const key of candidates) {
    const text = record[key];
    if (typeof text === 'string' && text.trim()) {
      return text.trim();
    }
  }
  return '';
}

/**
 * Reads device-specific page title.
 * Mobile uses `pageNameMobile` when that key exists; otherwise falls back to desktop `pageName`.
 */
export function parseCategoryPageNameForDevice(
  payload: Record<string, unknown> | null | undefined,
  preferredLang?: string,
  device: CategoryLayoutDevice = 'desktop',
): string {
  if (device === 'mobile' && payload && Object.prototype.hasOwnProperty.call(payload, CATEGORY_PAGE_NAME_MOBILE_PAYLOAD_KEY)) {
    return parseCategoryPageName(
      { [CATEGORY_PAGE_NAME_PAYLOAD_KEY]: payload[CATEGORY_PAGE_NAME_MOBILE_PAYLOAD_KEY] },
      preferredLang,
    );
  }
  return parseCategoryPageName(payload, preferredLang);
}

/** Re-export shared parser when callers want our-products defaults. */
export {
  parseAllProductsViewPorts,
  DEFAULT_ALL_PRODUCTS_VIEW_PORTS,
  parseDefaultViewPortKey,
  parseViewPortOrder,
  resolveDefaultViewMode,
  VIEW_PORT_KEYS,
};
export type { ViewPortKey } from './our-products-layout.util';
